/**
 * Platform-members API wrapper (dink-api `GET /v2/admin/platform-members`, read-only).
 *
 * Lists the tenant's saas-auth **login identities** — who has a login to this
 * tenant and at what role/status (owners, admins, users, pending invites, revoked
 * logins). This is a DISTINCT axis from the operational Members screen,
 * which lists booking contacts: a person may be a booking contact with no login,
 * or a login with no booking contact. The two are NOT merged or cross-linked
 * (DNKADM-0015 §Non-Goals).
 *
 * The endpoint is role-gated OWNER/ADMIN and resolves the tenant **server-side**
 * from the JWT `tenant_slug` — there is NO tenant parameter (a client-sent slug is
 * ignored). It is an ordinary `/v2/admin/**` bearer call: the `request()` primitive
 * attaches the bearer token only (no cookies, no CSRF) and applies the bounded
 * 401→refresh→retry. The server-to-server key dink-api uses to reach saas-auth
 * lives only in dink-api's backend — nothing for the browser to hold.
 *
 * Authoritative contract:
 *  - List GET /v2/admin/platform-members
 *         → { tenantSlug, members: [{
 *              userId, email, name, role, status, kind, state, optional contact id,
 *              locationAccess: { allLocations, locationIds }
 *            }] }
 *         ordered by `email` (case-insensitive); re-sort client-side if needed.
 *         Since DNKAPI-0517 (handoff §4) every row says what it IS via `kind`
 *         (`STAFF`/`CUSTOMER`) with `state` (`GUEST`/`USER`) on customer rows; staff
 *         rows come first and never carry a contact id, and a person who is both is TWO
 *         rows with the SAME `userId` (see `@/domain/rosterKind`).
 *  - Read location access:
 *         GET /v2/admin/platform-members/{userId}/location-access
 *         → { userId, allLocations, locationIds }
 *  - Set location access:
 *         PUT /v2/admin/platform-members/{userId}/location-access
 *         ← { allLocations:true } OR { allLocations:false, locationIds }
 *
 * Parsing rules:
 *  - DEFENSIVE on fields: a missing/wrong-typed `email`/`name` degrades to a
 *    placeholder string; it never throws.
 *  - TOLERANT of unknown enums: `role`/`status` are typed as a known union OR any
 *    string, so a future saas-auth value is preserved raw for display, not crashed.
 *  - FAIL-CLOSED on the roster: a non-array `members` (or a non-200, which
 *    `request()` already throws on) is treated as UNKNOWN/error — never as an empty
 *    roster. dink-api fails closed on an upstream saas-auth fault and does NOT
 *    return a half/empty list, so "0 members" only ever means a genuine empty 200.
 *
 * NOTE (pending confirmation — DNKADM-0015 §5): the deployed dink-api Swagger
 * (`/v3/api-docs`) was unreachable during this build (Operator-approved defensive
 * build), so the path, OWNER/ADMIN+JWT-tenant gating, field casing, the exact enum
 * literals, optional `?page&pageLimit` paging, and that upstream faults surface as a
 * 5xx (not a 200 with an empty list) are all bound to the FIXED §2 contract and must
 * be reconciled against the live OpenAPI once reachable. No paging params are sent
 * today — the roster returns in full (the lean shape carries no paging envelope).
 */
import {
  parseCustomerState,
  parseRosterKind,
  type CustomerState,
  type RosterKind,
} from '@/domain/rosterKind';
import { ApiError } from './errors';
import { request } from './client';

/**
 * Known platform roles. A future saas-auth value still parses (see `PlatformRole`).
 * `STAFF` is the new floor role (handoff §3); `SUPER_ADMIN` stays in the KNOWN set — it
 * is no longer storable as a tenant role, but it still arrives on old rows and on a
 * support session's audit trail, and must render rather than fall through to neutral.
 */
type KnownPlatformRole = 'OWNER' | 'SUPER_ADMIN' | 'ADMIN' | 'STAFF' | 'USER' | 'GUEST';
/** Known account statuses. A future saas-auth value still parses (see `PlatformStatus`). */
type KnownPlatformStatus = 'ACTIVE' | 'INVITED' | 'DISABLED';

/**
 * Role on the wire. Widened to `string` so an unknown future enum is preserved
 * raw rather than crashing the table — the `(string & {})` keeps the known
 * literals as autocomplete hints while still admitting any string.
 */
type PlatformRole = KnownPlatformRole | (string & {});
/** Status on the wire — widened like `PlatformRole` to tolerate unknown enums. */
type PlatformStatus = KnownPlatformStatus | (string & {});

/** One login identity. `role`/`status` may be an unknown future enum (rendered raw). */
export interface PlatformMember {
  /**
   * The login's identifier — NOT unique on its own since DNKAPI-0517: a person who is
   * both a customer and staff is TWO rows with the SAME `userId` and different `kind`,
   * so a React key must be `userId` + `kind` (handoff §4).
   */
  userId: number;
  email: string;
  name: string;
  /**
   * Role on the wire. Staff rows carry the ladder (`STAFF | ADMIN | OWNER`); a customer
   * row carries a legacy role only until dink-api's roster-source flip and then NONE —
   * an absent role parses to `''` and is NEVER defaulted to `'USER'`. Derive what to
   * display with `rosterDisplayRole` (`@/domain/rosterKind`).
   */
  role: PlatformRole;
  status: PlatformStatus;
  /** What this row IS (handoff §4). `null` on a row from before the field shipped. */
  kind: RosterKind;
  /** A customer row's account state (`GUEST`/`USER`); `null` on staff and pre-0517 rows. */
  state: CustomerState;
  /** Optional linked contact id, present when saas-auth/dink-api can link one. */
  contactId?: string;
  /** Per-platform-user location access. Legacy rows that omit it default to all locations. */
  locationAccess: PlatformMemberLocationAccess;
}

/** Location access annotation for a platform user. */
export interface PlatformMemberLocationAccess {
  allLocations: boolean;
  locationIds: string[];
}

/**
 * The roles an OWNER may assign/invite on the write surface (DNKADM-0167, extended by
 * DNKADM-0360 / handoff §3): the STAFF LADDER only. `STAFF` joins the set; `USER` leaves
 * it — a customer is a `kind`, not a staff role, and is invited from the Users screen
 * (`/v2/admin/users/invitations`, role forced to USER). `SUPER_ADMIN` and `GUEST` are
 * rejected upstream (`400 ROLE_NOT_ALLOWED`) and were never part of this set — an OWNER
 * may create/promote another OWNER. This is the assignable subset; the roster's
 * `PlatformRole` stays widened for READ display of any existing/unknown role.
 */
export type AssignableRole = 'OWNER' | 'ADMIN' | 'STAFF';

/**
 * The statuses an OWNER may set on the write surface (DNKADM-0167). Disable =
 * remove all access (there is no zero-locations / hard-delete state); a disabled
 * user's `locationAccess` persists so re-enabling (`ACTIVE`) restores it.
 */
export type SettableStatus = 'ACTIVE' | 'DISABLED';

/** Known elevated-invitation lifecycle states (DNKADM-0167). */
type KnownInvitationStatus = 'PENDING' | 'ACCEPTED' | 'REVOKED' | 'EXPIRED';
/** Invitation status on the wire — widened like {@link PlatformRole} to tolerate unknown enums. */
export type InvitationStatus = KnownInvitationStatus | (string & {});

/**
 * One pending/settled elevated invitation (DNKADM-0167 —
 * `GET/POST …/platform-members/invitations`). A distinct axis from the canonical
 * {@link PlatformMember} roster row: an invitation exists only until it is accepted
 * (then the invitee appears in the roster and is managed via role/status/
 * location-access, not the invitation endpoints). Ids are normalized to strings via
 * {@link asId}; text fields degrade to `null` and `role`/`status` are preserved RAW
 * so an unknown future enum renders rather than crashing. `invitedByUserId` is a
 * numeric platform `userId` (join the roster to show a name).
 */
export interface PlatformInvitation {
  /** Stable invitation id — the key for cancel/resend. */
  invitationId: string;
  email: string | null;
  role: PlatformRole;
  status: InvitationStatus;
  /** The platform `userId` of the inviter (join the roster for a name), or null. */
  invitedByUserId: string | null;
  /** ISO-8601 UTC creation timestamp, or null. */
  createdAt: string | null;
  /** ISO-8601 UTC expiry timestamp, or null. */
  expiresAt: string | null;
  /**
   * DNKAPI-0661: the location access chosen at invite time that is still pending (applied when
   * the invitee joins). `null` when none was chosen, it was already applied, or it is dead.
   * OPTIONAL: an older API omits the field, and the row then renders as it always did.
   */
  locationAccess?: PlatformMemberLocationAccess | null;
}

/** Parsed `GET /v2/admin/platform-members/{userId}/location-access` response. */
export interface PlatformMemberLocationAccessResponse extends PlatformMemberLocationAccess {
  userId: number;
}

/** Body for `PUT /v2/admin/platform-members/{userId}/location-access`. */
export type SetPlatformMemberLocationAccessBody =
  | { allLocations: true }
  | { allLocations: false; locationIds: string[] };

/** Parsed `GET /v2/admin/platform-members` response. */
export interface PlatformMembersResponse {
  /** The server-derived tenant slug the roster belongs to (display/diagnostic). */
  tenantSlug: string | null;
  members: PlatformMember[];
}

// --- Defensive parsing helpers -------------------------------------------------

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** A trimmed non-empty string, or null. */
function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/** A finite number (accepts a numeric string), or null. */
function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/**
 * A stable string id from a NUMBER or a string. v2 entity ids often arrive as
 * numbers on the wire and are normalized to strings for the UI.
 */
function asId(value: unknown): string | null {
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  return asText(value);
}

/** A boolean from a real boolean, or a fallback when absent/unparseable. */
function asBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function parseLocationIds(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const out: string[] = [];
  for (const entry of value) {
    const id = asId(entry);
    if (id !== null) out.push(id);
  }
  return out;
}

function parseLocationAccess(
  raw: unknown,
  absentFallback: PlatformMemberLocationAccess,
): PlatformMemberLocationAccess {
  if (!isObject(raw)) return absentFallback;
  return {
    allLocations: asBoolean(raw['allLocations'], false),
    locationIds: parseLocationIds(raw['locationIds']),
  };
}

function parseLocationAccessRead(
  raw: unknown,
  fallbackUserId: unknown,
): PlatformMemberLocationAccessResponse {
  const o = isObject(raw) ? raw : {};
  return {
    userId: asNumber(o['userId']) ?? asNumber(fallbackUserId) ?? 0,
    allLocations: asBoolean(o['allLocations'], false),
    locationIds: parseLocationIds(o['locationIds']),
  };
}

function defaultLocationAccess(): PlatformMemberLocationAccess {
  return { allLocations: true, locationIds: [] };
}

/**
 * Parse one member row defensively. `userId` falls back to its array index only when the
 * wire omits it; `email`/`name` degrade to a placeholder; `role`/`status` are preserved
 * RAW (any string survives) so an unknown future enum renders rather than crashing — an
 * absent role/status becomes an empty string the screen treats as "unknown", NEVER
 * `'USER'` (DNKADM-0360: a post-flip customer row carries no role at all). `kind`/`state`
 * parse by value; anything else is `null`, which keeps the role fallback in play for a
 * row that predates DNKAPI-0517.
 */
function parseMember(raw: unknown, index: number): PlatformMember {
  const o = isObject(raw) ? raw : {};
  const member: PlatformMember = {
    userId: asNumber(o['userId']) ?? index,
    email: asText(o['email']) ?? '',
    name: asText(o['name']) ?? '',
    role: asText(o['role']) ?? '',
    status: asText(o['status']) ?? '',
    kind: parseRosterKind(o['kind']),
    state: parseCustomerState(o['state']),
    locationAccess:
      'locationAccess' in o
        ? parseLocationAccess(o['locationAccess'], { allLocations: false, locationIds: [] })
        : defaultLocationAccess(),
  };
  const contactId = asText(o['crmContactId']); // Linked-contact wire key mapped to contactId.
  if (contactId !== null) member.contactId = contactId;
  return member;
}

/**
 * Parse one invitation row defensively (mirrors {@link parseMember}). `invitationId`
 * degrades to an empty string when the wire omits it (never a throw); `email` and
 * the timestamps degrade to `null`; `role`/`status` are preserved RAW so an unknown
 * future enum survives for display.
 */
function parseInvitation(raw: unknown): PlatformInvitation {
  const o = isObject(raw) ? raw : {};
  return {
    invitationId: asId(o['invitationId']) ?? '',
    email: asText(o['email']),
    role: asText(o['role']) ?? '',
    status: asText(o['status']) ?? '',
    invitedByUserId: asId(o['invitedByUserId']),
    createdAt: asText(o['createdAt']),
    expiresAt: asText(o['expiresAt']),
    ...parsePendingAccess(o),
  };
}

/** The invitation's pending `locationAccess`: set only when the wire carries the field (null stays null). */
function parsePendingAccess(o: Record<string, unknown>): {
  locationAccess?: PlatformMemberLocationAccess | null;
} {
  if (!('locationAccess' in o)) return {};
  const raw = o['locationAccess'];
  if (!isObject(raw)) return { locationAccess: null };
  return {
    locationAccess: {
      allLocations: asBoolean(raw['allLocations'], false),
      locationIds: parseLocationIds(raw['locationIds']),
    },
  };
}

/**
 * Tolerantly extract the invitation array from a list body: a bare array or an
 * `invitations` wrapper. A non-array (or missing) yields `[]` rather than a throw
 * (mirrors `reservations.ts` `extractReservations`).
 */
function extractInvitations(body: unknown): unknown[] {
  if (Array.isArray(body)) return body;
  if (isObject(body) && Array.isArray(body['invitations'])) return body['invitations'];
  return [];
}

// --- Endpoint ------------------------------------------------------------------

/**
 * `GET /v2/admin/platform-members` → the tenant's login roster.
 *
 * Bearer-only `/v2/admin/**` call via `request()` (no paging params today). Throws
 * `ApiError` on a non-2xx (the caller renders the fail-closed error+retry state;
 * a `403` renders the dedicated not-authorized state). FAIL-CLOSED: a `200` whose
 * `members` is not an array is itself treated as an error (UNKNOWN roster) — it is
 * NEVER coerced to an empty list, because dink-api fails closed upstream and does
 * not emit a half/empty roster. A genuine empty `200` (an array of length 0) is a
 * valid empty roster and parses to `members: []`.
 */
export async function getPlatformMembers(): Promise<PlatformMembersResponse> {
  const body = await request<unknown>('GET', '/v2/admin/platform-members');
  const o = isObject(body) ? body : {};
  const rawMembers = o['members'];
  if (!Array.isArray(rawMembers)) {
    // Fail-closed: an unparseable roster is UNKNOWN, never empty.
    throw new ApiError('UNKNOWN', 200, {
      details: { message: 'Platform-members roster was missing or not an array.' },
    });
  }
  return {
    tenantSlug: asText(o['tenantSlug']),
    members: rawMembers.map(parseMember),
  };
}

function locationAccessPath(userId: number | string): string {
  return `/v2/admin/platform-members/${encodeURIComponent(String(userId))}/location-access`;
}

/**
 * `GET /v2/admin/platform-members/{userId}/location-access` → the user's current
 * location access. Throws the shared typed `ApiError` on non-2xx responses.
 */
export async function getLocationAccess(
  userId: number | string,
): Promise<PlatformMemberLocationAccessResponse> {
  const body = await request<unknown>('GET', locationAccessPath(userId));
  return parseLocationAccessRead(body, userId);
}

function toSetLocationAccessBody(
  body: SetPlatformMemberLocationAccessBody,
): Record<string, unknown> {
  if (body.allLocations) return { allLocations: true };
  return { allLocations: false, locationIds: body.locationIds };
}

/**
 * `PUT /v2/admin/platform-members/{userId}/location-access` — replace the user's
 * location access with all locations, or with the provided specific ids. Non-2xx
 * responses propagate as typed `ApiError`s; callers map 400/403/404 inline.
 */
export async function setLocationAccess(
  userId: number | string,
  body: SetPlatformMemberLocationAccessBody,
): Promise<void> {
  await request<unknown>('PUT', locationAccessPath(userId), {
    json: toSetLocationAccessBody(body),
  });
}

// --- Write surface (DNKADM-0167, OWNER-only) -----------------------------------
//
// All bearer-only `/v2/admin/**` calls via `request()` (bearer token, NO CSRF, NO
// cookies, the bounded 401→refresh→retry, the `X-Session-Mode` mutating-audit stamp
// on the writes). Tenant is server-derived from the JWT — NEVER send a tenant/slug/
// locationId. Non-2xx propagates as a typed `ApiError`; the business `code` lands on
// `ApiError.details.code` (see `apiErrorFromBody` in `client.ts`) and is mapped by
// `toPlatformMemberWriteErrorView` (`platformMembersError.ts`).

/** Base path for the elevated-invitation endpoints. */
const INVITATIONS_PATH = '/v2/admin/platform-members/invitations';

function invitationPath(invitationId: string): string {
  return `${INVITATIONS_PATH}/${encodeURIComponent(invitationId)}`;
}

function memberRolePath(userId: number | string): string {
  return `/v2/admin/platform-members/${encodeURIComponent(String(userId))}/role`;
}

function memberStatusPath(userId: number | string): string {
  return `/v2/admin/platform-members/${encodeURIComponent(String(userId))}/status`;
}

/** Body for {@link invitePlatformMember}. `role` is an assignable role; `name` optional. */
export interface InvitePlatformMemberInput {
  email: string;
  role: AssignableRole;
  name?: string;
  /**
   * DNKAPI-0661: the location access to hold for the invitee (`{allLocations:true}` or
   * `{allLocations:false, locationIds}`, the location-access PUT's shape). Omit it for OWNER
   * and for a customer invite; leaving it out keeps today's behavior.
   */
  locationAccess?: SetPlatformMemberLocationAccessBody;
}

/**
 * `POST /v2/admin/platform-members/invitations` ← `{ email, role, name? }` → the
 * created {@link PlatformInvitation}. `role` is a STAFF-ladder role (`STAFF` included
 * since handoff §3). The invitation's `target` (STAFF|CUSTOMER) is SERVER-derived and
 * never client-supplied — we send only `email`, `role`, `name?` and `locationAccess?`. The
 * email is sent SERVER-side (the response's `inviteUrl` is informational and
 * deliberately ignored). DNKAPI-0661: an optional
 * `locationAccess` is sent in the location-access PUT's shape and held server-side until the
 * invitee joins (a 400 INVALID_REQUEST creates no invitation). Throws a typed `ApiError` (e.g. `409 ALREADY_ACTIVE_MEMBER` /
 * `INVITATION_ALREADY_PENDING`, `400 ROLE_NOT_ALLOWED`).
 */
export async function invitePlatformMember(
  input: InvitePlatformMemberInput,
): Promise<PlatformInvitation> {
  const json: Record<string, unknown> = { email: input.email, role: input.role };
  if (input.name !== undefined) json['name'] = input.name;
  if (input.locationAccess !== undefined) {
    json['locationAccess'] = toSetLocationAccessBody(input.locationAccess);
  }
  const body = await request<unknown>('POST', INVITATIONS_PATH, { json });
  return parseInvitation(body);
}

/**
 * `GET /v2/admin/platform-members/invitations[?status=…]` → the tenant's invitations
 * (defensively parsed). `status` (`PENDING|ACCEPTED|REVOKED|EXPIRED`) is forwarded
 * ONLY when set; omitted returns all. The endpoint returns every role — the caller
 * filters to the STAFF LADDER for the Invited tab (DNKADM-0360; USER/GUEST invites
 * belong to the Users screen, DNKADM-0168). Throws a typed `ApiError` on non-2xx.
 */
export async function listPlatformInvitations(
  status?: InvitationStatus,
): Promise<PlatformInvitation[]> {
  const options = status ? { query: { status } } : undefined;
  const body = await request<unknown>('GET', INVITATIONS_PATH, options);
  return extractInvitations(body).map(parseInvitation);
}

/**
 * `DELETE /v2/admin/platform-members/invitations/{invitationId}` → `204` (idempotent
 * soft-revoke). Resolves to `void`. Throws a typed `ApiError` on non-2xx.
 */
export async function cancelPlatformInvitation(invitationId: string): Promise<void> {
  await request<unknown>('DELETE', invitationPath(invitationId));
}

/**
 * `POST /v2/admin/platform-members/invitations/{invitationId}/resend` → a FRESH
 * {@link PlatformInvitation} (rotates the token, resets expiry, re-sends the email).
 * Replace the row with the returned invitation. Throws a typed `ApiError` on non-2xx.
 */
export async function resendPlatformInvitation(invitationId: string): Promise<PlatformInvitation> {
  const body = await request<unknown>('POST', `${invitationPath(invitationId)}/resend`);
  return parseInvitation(body);
}

/**
 * `PUT /v2/admin/platform-members/{userId}/role` ← `{ role }` → the canonical
 * {@link PlatformMember} row (drop it into the roster in place; promoting to OWNER
 * flips `locationAccess.allLocations:true`). Throws a typed `ApiError` (e.g.
 * `409 LAST_OWNER_PROTECTED` / `SELF_MODIFICATION_FORBIDDEN`, `400 ROLE_NOT_ALLOWED`,
 * `404 MEMBER_NOT_FOUND`).
 */
export async function setPlatformMemberRole(
  userId: number | string,
  role: AssignableRole,
): Promise<PlatformMember> {
  const body = await request<unknown>('PUT', memberRolePath(userId), { json: { role } });
  return parseMember(body, 0);
}

/**
 * `PUT /v2/admin/platform-members/{userId}/status` ← `{ status }` → the canonical
 * {@link PlatformMember} row. Disable = remove all access (no zero-locations state,
 * no hard-delete); a disabled user's `locationAccess` persists so re-enabling
 * (`ACTIVE`) restores it. Throws a typed `ApiError` (e.g. `409 LAST_OWNER_PROTECTED` /
 * `SELF_MODIFICATION_FORBIDDEN`, `400 STATUS_NOT_ALLOWED`, `404 MEMBER_NOT_FOUND`).
 */
export async function setPlatformMemberStatus(
  userId: number | string,
  status: SettableStatus,
): Promise<PlatformMember> {
  const body = await request<unknown>('PUT', memberStatusPath(userId), { json: { status } });
  return parseMember(body, 0);
}
